import { tickDerived } from '../../derived';
import type { EntityId, SimContext, SimState, SystemFn } from '../../types';
import { BUILD_TASK } from '../../workTargets';
import type { TickCache } from './cache';
import { MAX_LOOKED, SCOUT_MAX_AVOID, stepScout } from './scout';
import {
  cityTaskCounts,
  cityWorkerPlaces,
  isPinned,
  stepAssigned,
  stepWorker,
  type WorkerPlace,
} from './worker';

/**
 * Automatic behaviour for units with `auto` set (rules `automate`):
 *
 * - explore (scouts): walk to the *frontier* tile — an explored, walkable
 *   tile next to unexplored map — with the most unexplored map in vision of
 *   it for the walk, discounted near other scouts' targets and positions, so
 *   scouts head for big unexplored pockets and split up (see stepScout).
 *   Paths go round what the owner sees of enemy fighters and firing rival
 *   buildings and avoid seen rival territory; a scout threatened outside its
 *   shelter runs to safety first. Only explored tiles are ever targeted and
 *   only what the owner sees now is used, so the automation never acts on
 *   anything hidden by fog.
 * - work (workers): each worker has a home city (`homeCityId`) and only
 *   takes work in its territory: stay on a tile that yields something;
 *   otherwise walk to the best explored free work there: own unfinished
 *   sites first, then the gathering place (a wild tile, or a place in an
 *   own farm/forestry/mine) with the best `workValue`: its throughput
 *   (`throughput`: a load over the time to fill it there plus the walk to
 *   the city and back, so a rich wild tile by the city beats a far farm,
 *   and of two alike the nearer wins) shared out over the home city's
 *   workers on that resource, weighted by the city's `workFocus` (see
 *   `pickWork`) once the city's `workTargets` (pinned and auto workers
 *   counted) are met, resources short of a target coming first. A wild
 *   tile or site takes one worker, a farm its `workerSlots` (it works from inside the farm, see occupancy.ts). A
 *   worker arriving on a site is handed the build by the construction
 *   system (and stays automated); once built it works the building. A
 *   working worker moves at once for a site at home, and every
 *   REVIEW_TICKS (in its own phase) for gathering work clearly better than
 *   its own (SWITCH_MARGIN). Only when its home has no free work at all
 *   does it take work at the nearest own city that has some, which becomes
 *   its home (a fallen home is replaced by the nearest own city).
 * - haul (workers): gathering fills the worker's load; once full (or it
 *   stops filling where it stands, e.g. its tile ran dry) it leaves its farm
 *   and walks to a free tile next to its home city (else the nearest own city
 *   by path; inside the city when every tile beside it is taken), waits
 *   while the economy unloads it, then
 *   goes back to work, preferring the tile it came from, which it keeps
 *   claimed meanwhile (`auto.phase` 'haul', `auto.work`), unless other work
 *   is clearly better (SWITCH_MARGIN): settled workers then spread to a new
 *   mine, farm or forestry.
 * - retreat (workers): own units can't be hurt anywhere in their completed
 *   city's territory (garrison). When a visible enemy fighter comes within
 *   THREAT_RADIUS of a worker outside that territory, it drops its job and
 *   goes inside its nearest own city (else to the nearest territory tile);
 *   one already in the territory keeps working. While any visible enemy
 *   fighter is within SAFE_RADIUS, workers only pick (and keep walking to)
 *   work inside the territory, sheltering in a city when none is free there,
 *   and one sheltering in a city stays inside.
 *
 * - pinned (workers): an `assign` command can pin a worker to one job (a
 *   gather order or a build order with `pin`) instead: no `auto`, so none of
 *   the above moves it. It walks there, gathers, hauls to its city when full
 *   and comes back, indefinitely (see `stepAssigned`); the slots it holds
 *   (`claimCell`) are not offered to auto workers, and it doesn't retreat. A
 *   pinned builder carries on to the finished building (construction.ts).
 *
 * Workers only pick tiles they can stop on (one unit per tile, see
 * occupancy.ts); a worker's own claim is kept by `auto` and claims.
 *
 * Units only re-plan when they have nothing useful to do (or their target
 * stopped being useful), and a failed search backs off for RETRY_TICKS.
 * Runs before movement so a new path starts advancing the same tick.
 */

function newCache(state: SimState, ctx: SimContext): TickCache {
  return {
    derived: tickDerived(state, ctx),
    owners: new Map(),
    buildingsAt: null,
    cityTiles: null,
    searches: new Map(),
  };
}

/** One task of a city's worker panel: its target and who is on it now. */
interface TaskLine {
  task: string;
  target: number;
  pinned: number;
  auto: number;
  /** Other cities' workers working in this city's territory. */
  visiting: number;
}

/**
 * For an own city's panel: per task (each resource, then BUILD_TASK) the
 * `workTargets` target and the city's workers on it, pinned and auto,
 * counted as `pickWork` counts them (a worker counts for the city it
 * works for; `visiting` are other cities' workers in this city's
 * territory). Tasks with neither target nor workers are left out. Reads the city's own workers only (nothing hidden by fog).
 */
function workTaskLines(state: SimState, ctx: SimContext, cityId: EntityId): TaskLine[] {
  return workBoard(state, ctx, cityId).lines;
}

/**
 * `workTaskLines` plus where each counted worker stands in the task
 * (`WorkerPlace`: task, kind and held tile), from one pass: the city's
 * workers and visitors (`places`) and its own workers working in another
 * city (`away`). Reads own units only.
 */
function workBoard(
  state: SimState,
  ctx: SimContext,
  cityId: EntityId
): { lines: TaskLine[]; places: WorkerPlace[]; away: WorkerPlace[] } {
  const city = state.buildings[cityId];
  if (!city) return { lines: [], places: [], away: [] };
  const cache = newCache(state, ctx);
  const counts = cityTaskCounts(state, ctx, cache, cityId);
  const lines: TaskLine[] = [];
  for (const task of [...Object.keys(ctx.rules.resources), BUILD_TASK]) {
    const n = counts.get(task);
    const line = {
      task,
      target: city.workTargets?.[task] ?? 0,
      pinned: n?.pinned ?? 0,
      auto: n?.auto ?? 0,
      visiting: n?.visiting ?? 0,
    };
    if (line.target + line.pinned + line.auto + line.visiting > 0) lines.push(line);
  }
  return { lines, ...cityWorkerPlaces(state, ctx, cache, cityId) };
}

const automation: SystemFn = (state, ctx) => {
  const cache = newCache(state, ctx);
  for (const unit of Object.values(state.units)) {
    // A pinned worker (`assign`) runs its own job loop, not the auto one.
    if (!unit.auto) {
      if (isPinned(unit)) stepAssigned(state, ctx, cache, unit);
      continue;
    }
    const mode = ctx.rules.units[unit.type]?.automate;
    if (mode === 'explore') stepScout(state, ctx, cache, unit);
    else if (mode === 'work') stepWorker(state, ctx, cache, unit);
    else delete unit.auto;
  }
};

export {
  automation,
  MAX_LOOKED,
  SCOUT_MAX_AVOID,
  type TaskLine,
  type WorkerPlace,
  workBoard,
  workTaskLines,
};
